import React, { useContext, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import useLogout from '../../Authentication/components/hooks/useLogout';

import AppSidebar from './AppSidebar';
import UserMenu from './UserMenu';
import OmniSearch from './OmniSearch';
import useFeatureFlags from '../../Features/components/hooks/useFeatureFlags';
import FeatureFlagContext from '../../Features/components/FeatureFlagContext';
import OrgContext from '../../Authentication/components/OrgContext';
import AuthContext from '../../Authentication/components/AuthContext';
import { Toaster } from '@/src/shadComponents/ui/sonner';
import { useLocalStorage } from './hooks/useLocalStorage';
import FeatureFlag from '@/src/Features/components/FeatureFlag';
import { FeatureFlags } from '@/src/Features/lib/constants';
import LoadingIndicator from './LoadingIndicator';
import {
    SidebarInset,
    SidebarProvider,
} from '@/src/shadComponents/ui/sidebar';
import { SecondaryNavProvider, useSecondaryNav } from './SecondaryNavContext';
import { ThemeProvider } from './ThemeProvider';

export default function Layout() {
    const {
        auth: { orgs },
    } = useContext(AuthContext);
    const [org, setOrg] = useLocalStorage('current-org', orgs?.[0]);

    useEffect(() => {
        const doesOrgExistForCurrentUser = orgs?.find(
            (o: any) => o.id === org?.id,
        );
        if (!doesOrgExistForCurrentUser) {
            setOrg(orgs?.[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgs]);

    const logout = useLogout();
    return (
        <OrgContext.Provider value={{ org, setOrg }}>
            <FeatureFlagWrapper logout={logout} setOrg={setOrg} />
        </OrgContext.Provider>
    );
}

function FeatureFlagWrapper({
    logout,
    setOrg,
}: {
    logout: () => void;
    setOrg: (org: any) => void;
}) {
    const { featureFlags, isLoading } = useFeatureFlags();
    if (isLoading) {
        return (
            <div className="bg-background mx-auto h-[100vh] text-foreground">
                <div className="grid place-items-center px-6 py-24 sm:py-32 lg:px-8 h-full">
                    <LoadingIndicator />
                </div>
            </div>
        );
    }
    return (
        <FeatureFlagContext.Provider value={featureFlags}>
            <FeatureFlag
                flag={FeatureFlags.HAS_PHOTOSTAT_ACCESS}
                fallback={() => {
                    return (
                        <div className="bg-background mx-auto h-[100vh] text-foreground">
                            <div className="grid place-items-center px-6 py-24 sm:py-32 lg:px-8 h-full">
                                <div className="text-center">
                                    <h1 className="mt-4 text-5xl font-semibold sm:text-7xl text-foreground">
                                        Uh oh!
                                    </h1>
                                    <p className="mt-6 text-lg font-medium sm:text-xl/8 text-foreground">
                                        Looks like you're somewhere you
                                        shouldn't be. <br />
                                        Let's get you back to where you belong.
                                    </p>
                                    <div className="mt-10 flex items-center justify-center gap-x-6">
                                        <button
                                            className="dark-button underline"
                                            onClick={() => {
                                                window.location.href =
                                                    'https://app.photoengineers.com';
                                            }}
                                        >
                                            Go back home
                                        </button>
                                        <a
                                            href="mailto:support@photoengineers.com"
                                            className="text-sm font-semibold text-foreground"
                                        >
                                            Contact support{' '}
                                            <span aria-hidden="true">
                                                &rarr;
                                            </span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                }}
            >
                <ThemeProvider>
                    <SecondaryNavProvider>
                        <SidebarShell
                            logout={() => {
                                setOrg(null);
                                logout();
                            }}
                        />
                    </SecondaryNavProvider>
                </ThemeProvider>
            </FeatureFlag>
        </FeatureFlagContext.Provider>
    );
}

function SidebarShell({ logout }: { logout: () => void }) {
    const { hasSecondaryNav } = useSecondaryNav();
    const [sidebarOpen, setSidebarOpen] = useState(true);

    // Auto-collapse when secondary nav appears, auto-expand when it goes away
    useEffect(() => {
        setSidebarOpen(!hasSecondaryNav);
    }, [hasSecondaryNav]);

    return (
        <SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <AppSidebar />
            <SidebarInset>
                <header className="flex h-12 shrink-0 items-center border-b px-4">
                    <div className="flex-1" />
                    <OmniSearch />
                    <div className="flex-1 flex justify-end">
                        <UserMenu logout={logout} />
                    </div>
                </header>
                <div className="flex-1 overflow-auto" vaul-drawer-wrapper="">
                    <Outlet />
                </div>
            </SidebarInset>
            <Toaster position="bottom-left" />
        </SidebarProvider>
    );
}
